import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { createPageMeta } from '@/lib/meta';

export const Route = createFileRoute('/trpc-demo')({
  component: TRPCDemoPage,
  head: () => createPageMeta({
    title: 'tRPC Demo - Personal Website',
    description: 'Demonstration of tRPC integration with TanStack Start.',
    path: '/trpc-demo',
    image: '/og/trpc-demo.png',
  }),
});

function TRPCDemoPage() {
  const [name, setName] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const { data: users, isLoading: usersLoading } = useQuery(trpc.getUsers.queryOptions());

  const { data: greeting, refetch: refetchGreeting } = useQuery(
    trpc.hello.queryOptions({ name }, { enabled: false }),
  );

  const createUser = useMutation(trpc.createUser.mutationOptions({
    onSuccess: (data) => {
      alert(`User created: ${data.name}`);
      setUserName('');
      setUserEmail('');
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  }));

  const handleGreeting = () => {
    refetchGreeting();
  };

  const handleCreateUser = () => {
    if (!userName || !userEmail) {
      alert('Please fill in all fields');
      return;
    }
    createUser.mutate({
      name: userName,
      email: userEmail,
    });
  };

  return (
    <Container maxWidth="lg" className="py-8">
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            tRPC Integration Demo
          </Typography>
          <Typography variant="body1" color="text.secondary">
            End-to-end type-safe API with TanStack Start and tRPC v11
          </Typography>
        </Box>

        {/* Hello Query */}
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              1. Simple Query
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try the <code>hello</code> procedure with input
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Your Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <Button variant="contained" onClick={handleGreeting}>
                Get Greeting
              </Button>
              {greeting && (
                <Alert severity="success">
                  {greeting.greeting}
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Timestamp: {greeting.timestamp}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Users List Query */}
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              2. List Query
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fetch a list of users using <code>getUsers</code>
            </Typography>
            {usersLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1}>
                {users?.map((user) => (
                  <Paper key={user.id} elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {user.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Chip label={`ID: ${user.id}`} size="small" sx={{ mt: 1 }} />
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Create User Mutation */}
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              3. Mutation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new user with <code>createUser</code> mutation
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                variant="outlined"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleCreateUser}
                disabled={createUser.isPending}
              >
                {createUser.isPending ? 'Creating...' : 'Create User'}
              </Button>
              {createUser.isSuccess && (
                <Alert severity="success">
                  User created successfully! Check the console for details.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Code Example */}
        <Card sx={{ bgcolor: 'grey.100' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Usage Example
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                overflow: 'auto',
              }}
            >
              <code>{`import { useQuery, useMutation } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';

function MyComponent() {
  // Query
  const { data, isLoading } = useQuery(trpc.getUsers.queryOptions());

  // Mutation
  const createUser = useMutation(trpc.createUser.mutationOptions({
    onSuccess: (data) => {
      console.log('User created:', data);
    },
  }));

  return <div>{/* Your UI */}</div>;
}`}</code>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
